import type { SystemRole } from '../config/permissions';
import { SUPPORT_CENTER_DISPLAY_NAME } from '../services/assistantCentral';
import { isKlinviaNativeApp } from '../utils/nativeApp';

export type NavCatalogEntry = {
  id: string;
  label: string;
  path: string;
  group: string;
  keywords?: string[];
};

export type NavigationCatalogContext = {
  userRole: SystemRole | string;
  hasPermission: (path: string) => boolean;
  isFisioterapia: boolean;
  isOdontologia: boolean;
  plansEnabled: boolean;
  inventoryModuleEnabled: boolean;
  platformAdminEnabled: boolean;
  whatsappConfigVisible: boolean;
  /** Inboxes dinâmicos (Central de Mensagens) — text + path */
  inboxEntries?: Array<{ text: string; path: string }>;
  /** Profissional com inbox dinâmico sem rota /inbox */
  professionalDynamicInbox?: boolean;
};

type RawNavItem = {
  text: string;
  path?: string;
  group: string;
  roles?: SystemRole[];
  keywords?: string[];
  showWhen?: (ctx: NavigationCatalogContext) => boolean;
  submenu?: RawNavItem[];
};

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

function roleAllowed(item: RawNavItem, ctx: NavigationCatalogContext): boolean {
  if (item.showWhen && !item.showWhen(ctx)) return false;
  if (!item.roles?.length) return true;
  if (item.roles.includes(ctx.userRole as SystemRole)) return true;
  if (
    item.text === 'Central de Mensagens'
    && ctx.userRole === 'professional'
    && ctx.professionalDynamicInbox
  ) {
    return true;
  }
  return false;
}

function pathAllowed(path: string, ctx: NavigationCatalogContext): boolean {
  const base = path.split('?')[0];
  return ctx.hasPermission(path) || ctx.hasPermission(base);
}

function flattenRaw(items: RawNavItem[], ctx: NavigationCatalogContext, out: NavCatalogEntry[]): void {
  for (const item of items) {
    if (!roleAllowed(item, ctx)) continue;
    if (item.submenu?.length) {
      flattenRaw(item.submenu, ctx, out);
      continue;
    }
    if (!item.path || !pathAllowed(item.path, ctx)) continue;
    out.push({
      id: item.path,
      label: item.text,
      path: item.path,
      group: item.group,
      keywords: item.keywords,
    });
  }
}

function buildRawMenuTree(ctx: NavigationCatalogContext): RawNavItem[] {
  const inboxSub: RawNavItem[] = (ctx.inboxEntries || []).map((inbox) => ({
    text: inbox.text,
    path: inbox.path,
    group: 'Central de Mensagens',
    keywords: ['whatsapp', 'inbox', 'mensagens'],
  }));

  return [
    {
      text: 'Agenda',
      path: '/agenda',
      group: 'Principal',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
      keywords: ['calendario', 'consultas'],
    },
    {
      text: 'Auditoria diária',
      path: '/clinic/daily-audit',
      group: 'Principal',
      roles: ['admin', 'admin_master', 'receptionist', 'financial'],
    },
    {
      text: 'Sala de Espera',
      path: '/clinic/waiting-room',
      group: 'Principal',
      roles: ['professional'],
    },
    {
      text: 'Central de Mensagens',
      group: 'Mensagens',
      roles: ['admin', 'admin_master', 'receptionist'],
      submenu: inboxSub,
    },
    ...(ctx.platformAdminEnabled
      ? [{
        text: 'Inbox de suporte',
        path: '/platform-support',
        group: 'Mensagens',
        roles: ['admin_master'] as SystemRole[],
      }]
      : []),
    {
      text: 'Chat da Equipe',
      path: '/team-chat',
      group: 'Mensagens',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
      keywords: ['chat', 'equipe'],
    },
    {
      text: SUPPORT_CENTER_DISPLAY_NAME,
      path: '/central-ia',
      group: 'Suporte',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
      keywords: ['ia', 'assistente', 'central'],
    },
    {
      text: 'Central de Atendimento IA',
      path: '/support',
      group: 'Suporte',
      roles: ['admin', 'admin_master'],
    },
    ...(!ctx.isOdontologia ? [{
      text: 'Doctoralia',
      path: '/clinic/doctoralia',
      group: 'Integrações',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
    }] : []),
    {
      text: 'CRM',
      path: '/crm',
      group: 'CRM',
      roles: ['admin', 'admin_master', 'receptionist'],
    },
    {
      text: 'Pacientes',
      path: '/clinic/patients',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
      keywords: ['cadastro', 'prontuario'],
    },
    {
      text: 'Evoluções',
      path: '/clinic/evolutions',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'assistant'],
    },
    ...(ctx.isFisioterapia ? [{
      text: 'Avaliações',
      path: '/clinic/physiotherapy-assessments',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
    }] : []),
    ...(!ctx.isFisioterapia && !ctx.isOdontologia ? [{
      text: 'Relatórios Médicos',
      path: '/clinic/medical-reports',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
    }] : []),
    ...(!ctx.isFisioterapia ? [{
      text: 'Receituários',
      path: '/clinic/prescriptions',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist'] as SystemRole[],
      keywords: ['receita', 'prescricao'],
    }] : []),
    {
      text: 'Orçamentos',
      path: '/clinic/budgets',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial'],
    },
    ...(ctx.isOdontologia ? [{
      text: 'Planejamentos',
      path: '/clinic/plannings',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist'] as SystemRole[],
    }] : []),
    {
      text: 'Clínica',
      group: 'Clínica',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
      submenu: [
        { text: 'Dashboard Conversas', path: '/dashboard', group: 'Clínica', roles: ['admin', 'admin_master'] },
        { text: 'Sala de Espera', path: '/clinic/waiting-room', group: 'Clínica', roles: ['admin', 'admin_master', 'receptionist', 'assistant'] },
        { text: 'Procedimentos', path: '/clinic/procedures', group: 'Clínica' },
        ...(ctx.plansEnabled ? [{
          text: 'Planos',
          path: '/clinic/patient-plans',
          group: 'Clínica',
          roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial'] as SystemRole[],
        }] : []),
        { text: 'Origens de pacientes', path: '/clinic/patient-sources', group: 'Clínica' },
        { text: 'Profissionais', path: '/clinic/professionals', group: 'Clínica' },
        ...(!ctx.isFisioterapia ? [{ text: 'Receituários', path: '/clinic/prescriptions', group: 'Clínica', roles: ['admin', 'admin_master', 'professional', 'receptionist'] as SystemRole[] }] : []),
        ...(ctx.isFisioterapia ? [{ text: 'Avaliações', path: '/clinic/physiotherapy-assessments', group: 'Clínica' }] : []),
        ...(!ctx.isFisioterapia ? [
          { text: 'Pedidos de Exames', path: '/clinic/exam-orders', group: 'Clínica' },
          { text: 'Laboratório', path: '/clinic/laboratory', group: 'Clínica' },
          { text: 'Atestados', path: '/clinic/certificates', group: 'Clínica' },
        ] : []),
        ...(!ctx.isFisioterapia && !ctx.isOdontologia ? [{ text: 'Relatórios Médicos', path: '/clinic/medical-reports', group: 'Clínica' }] : []),
        { text: 'Encaminhamentos', path: '/clinic/referrals', group: 'Clínica' },
        { text: 'Assinatura de Documentos', path: '/clinic/document-signing', group: 'Clínica', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] },
        { text: 'Modelos de Contrato', path: '/clinic/contract-templates', group: 'Clínica', roles: ['admin', 'admin_master'] },
        { text: 'Convênios / TISS', path: '/clinic/insurance', group: 'Clínica' },
        { text: 'Galeria Clínica', path: '/clinic/image-gallery', group: 'Clínica' },
        ...(ctx.isOdontologia ? [
          { text: 'Doctoralia', path: '/clinic/doctoralia', group: 'Clínica', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[] },
          { text: 'Relatórios Médicos', path: '/clinic/medical-reports', group: 'Clínica' },
          { text: 'Odontograma', path: '/clinic/odontogram', group: 'Clínica', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
          { text: 'Periodontograma', path: '/clinic/periodontal-charts', group: 'Clínica', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
          { text: 'Tratamentos Odonto', path: '/clinic/dental-treatments', group: 'Clínica', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
          { text: 'Templates Planejamento', path: '/clinic/planning-templates', group: 'Clínica', roles: ['admin', 'admin_master'] as SystemRole[] },
        ] : []),
        { text: 'Confirmação Auto', path: '/clinic/reminder-settings', group: 'Clínica', keywords: ['lembrete', 'whatsapp'] },
        { text: 'Aniversariantes', path: '/clinic/birthdays', group: 'Clínica', roles: ['admin', 'admin_master', 'receptionist'] },
        { text: 'Config. Clínica', path: '/clinic/settings', group: 'Clínica', keywords: ['configuracoes', 'settings'] },
      ],
    },
    {
      text: 'Financeiro',
      group: 'Financeiro',
      roles: ['admin', 'admin_master', 'financial', 'receptionist'],
      submenu: [
        { text: 'Resumo', path: '/financial/dashboard', group: 'Financeiro' },
        { text: 'DRE Gerencial', path: '/financial/dre', group: 'Financeiro' },
        { text: 'Plano de Contas', path: '/financial/chart-of-accounts', group: 'Financeiro' },
        { text: 'Contas a Pagar', path: '/financial/accounts-payable', group: 'Financeiro' },
        { text: 'Contas a Receber', path: '/financial/accounts-receivable', group: 'Financeiro' },
        { text: 'Recebíveis de Cartão', path: '/financial/card-settlements', group: 'Financeiro' },
        { text: 'Caixa', path: '/financial/cash-register', group: 'Financeiro', keywords: ['pdv'] },
        { text: 'Extratos', path: '/financial/bank-statements', group: 'Financeiro', keywords: ['banco', 'conciliacao'] },
        { text: 'Repasses', path: '/financial/repasses', group: 'Financeiro', keywords: ['comissao'] },
        { text: 'Regras de Comissão', path: '/financial/commission-rules', group: 'Financeiro' },
        { text: 'Notas Fiscais', path: '/financial/nfse', group: 'Financeiro', keywords: ['nfse', 'nfe'] },
        { text: 'Recibos', path: '/financial/receipts', group: 'Financeiro' },
      ],
    },
    ...(ctx.inventoryModuleEnabled ? [{
      text: 'Estoque',
      group: 'Estoque',
      showWhen: () => ctx.inventoryModuleEnabled,
      submenu: [
        { text: 'Geral', path: '/inventory/items', group: 'Estoque' },
        { text: 'Categorias', path: '/inventory/categories', group: 'Estoque' },
        { text: 'Fornecedores', path: '/inventory/suppliers', group: 'Estoque' },
        { text: 'Movimentações', path: '/inventory/items?tab=movimentacoes', group: 'Estoque' },
      ],
    }] : []),
    {
      text: 'Relatórios',
      path: '/clinic/reports',
      group: 'Relatórios',
      roles: ['admin', 'admin_master', 'financial'],
    },
    {
      text: 'Unidades',
      path: '/admin/units',
      group: 'Administração',
      roles: ['admin', 'admin_master'],
    },
    {
      text: 'Gestão de Usuários',
      path: '/user-management',
      group: 'Administração',
      roles: ['admin_master'],
    },
    ...(isKlinviaNativeApp() ? [] : [{
      text: 'Plano da plataforma',
      path: '/platform-payments',
      group: 'Administração',
      roles: ['admin_master'] as SystemRole[],
    }]),
    ...(ctx.whatsappConfigVisible ? [{
      text: 'Config WhatsApp',
      group: 'Configurações',
      showWhen: () => ctx.whatsappConfigVisible,
      submenu: [
        { text: 'Cloud API', path: '/whatsapp-cloud-api', group: 'Configurações' },
        { text: 'Gerenciar APIs', path: '/whatsapp-auto', group: 'Configurações' },
        { text: 'QR Code', path: '/whatsapp-qr', group: 'Configurações' },
      ],
    }] : []),
    {
      text: 'Tutoriais',
      path: '/tutoriais',
      group: 'Ajuda',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
    },
  ];
}

export function buildNavigationCatalog(ctx: NavigationCatalogContext): NavCatalogEntry[] {
  const out: NavCatalogEntry[] = [];
  flattenRaw(buildRawMenuTree(ctx), ctx, out);
  const seen = new Set<string>();
  return out.filter((entry) => {
    if (seen.has(entry.path)) return false;
    seen.add(entry.path);
    return true;
  });
}

export function searchNavigationCatalog(
  query: string,
  entries: NavCatalogEntry[],
  limit = 15,
): NavCatalogEntry[] {
  const q = normalizeSearchText(query);
  if (!q) return entries.slice(0, limit);

  const scored = entries
    .map((entry) => {
      const label = normalizeSearchText(entry.label);
      const group = normalizeSearchText(entry.group);
      const path = normalizeSearchText(entry.path.replace(/\//g, ' '));
      const keywords = (entry.keywords || []).map(normalizeSearchText).join(' ');
      const haystack = `${label} ${group} ${path} ${keywords}`;
      if (label.startsWith(q)) return { entry, score: 100 };
      if (label.includes(q)) return { entry, score: 80 };
      if (keywords.includes(q)) return { entry, score: 70 };
      if (group.includes(q)) return { entry, score: 60 };
      if (path.includes(q)) return { entry, score: 50 };
      if (haystack.includes(q)) return { entry, score: 40 };
      return null;
    })
    .filter(Boolean) as Array<{ entry: NavCatalogEntry; score: number }>;

  scored.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label, 'pt-BR'));
  return scored.slice(0, limit).map((row) => row.entry);
}
